import { IReturn } from "@/interfaces/Return";
import React from "react";
import ProductCounter from "../product-counter/ProductCounter";
import { formateDate } from "@/utils/date-utils";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { PRIMARY_COLOR } from "@/constants/constants";
import { setReturn } from "@/store/reducers/returnReducer";
import { handleReturnClick } from "@/utils/return-navigation";
import { NavigationRoutes } from "../navigation/constants";
import { handleFileDownload } from "@/utils/file-download";
import { setReturnHeader } from "@/store/reducers/returnHeaderReducer";
import { openDialog } from "@/store/reducers/dialogReducer";
import DropOffTag from "../tags/DropoffTag";
import StatusTag from "../tags/StatusTag";
import SourceTag from "../tags/SourceTag";
import { DialogType } from "@/enums/dialogs";
import { ReturnActions } from "./ReturnActions"; // New external component
import ReturnTypesTag from "../tags/ReturnTypesTag";

interface ReturnProps {
  return_: IReturn;
  readOnly?: boolean;
}

const Return: React.FC<ReturnProps> = ({ return_, readOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const roles = useSelector((state: RootState) => state.authentication.roles);

  const handleReturnClick_ = () => {
    dispatch(setReturn(return_));

    // Consultation seule: on ouvre le détail sans passer par les flux de
    // validation liés au rôle et au statut
    if (readOnly) {
      navigate(NavigationRoutes.RETURN_DETAILS, { state: { readOnly: true } });
      return;
    }

    handleReturnClick(return_, roles, navigate);
  };

  // Pass action handlers to the external component
  const actionHandlers = {
    onLitigeClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(setReturn(return_));
      navigate(NavigationRoutes.RETURN_LITIGE);
    },
    onConsultLitigeClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(setReturn(return_));
      navigate(NavigationRoutes.LITIGE_DETAILS);
    },
    onDeleteClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(
        setReturnHeader({
          returnCode: return_.numTournee,
          seller: {
            sellerId: return_.sellerId,
            sellerName: return_.sellerName,
            isSellerVIP: return_.isSellerVIP,
          },
        }),
      );
      dispatch(openDialog(DialogType.DELETE_CONFIRMATION_DIALOG));
    },
    onDownloadClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (return_.url)
        handleFileDownload(return_.url, return_.numTournee.toString() || "");
    },
    onPDADownloadClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (return_.urlPathReceptionPartial)
        handleFileDownload(
          return_.urlPathReceptionPartial,
          `ReceptionPartielle_${return_.numTournee}`,
        );
    },
    onBLDownloadClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (return_.urlCachte)
        handleFileDownload(
          return_.urlCachte,
          return_.numTournee.toString() || "",
        );
    },
  };

  return (
    <div
      className={`w-full flex flex-col rounded-lg shadow-md bg-white hover:bg-gray-50 my-2 border border-${PRIMARY_COLOR} border-2 p-3 space-y-2 cursor-pointer transition-colors duration-200`}
      onClick={handleReturnClick_}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
        <h2 className="text-lg font-semibold text-gray-800 mb-1 sm:mb-0">
          Tournee N° {return_.numTournee}{" "}
          {return_.numDemande && ` - Demande ${return_.numDemande}`}
        </h2>
        <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-0">
          <ReturnTypesTag text={return_.typeTournee} />
          <SourceTag text={return_.sourceVendable} />
          <DropOffTag
            entityId={return_.entityId}
            entityName={return_.entityName}
          />
          <StatusTag
            text={return_.statutName}
            bakcgroundColor={return_.statutBackground}
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700">
          {return_.sellerName}
        </h3>
        <p className="text-xs text-gray-500">
          {formateDate(return_.createdon)}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ProductCounter productCount={return_.countArticles} />
        {!readOnly && (
          <ReturnActions
            return_={return_}
            roles={roles}
            handlers={actionHandlers}
          />
        )}
      </div>
    </div>
  );
};

export default Return;
